import { Card, CardPreview, makeStyles, mergeClasses, SkeletonItem, Text, tokens } from "@fluentui/react-components";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import pageService from "../../services/PageService";
import { Link } from "react-router-dom";
import Status from "../../enums/Status";
import { mergeStyles } from "@fluentui/merge-styles";
import { dolphinTokens } from "../../utils/Theme";
import IPagePreview from "../../models/page/IPagePreview";

const useStyles = makeStyles({
    contentWrapper: {
        paddingBottom: "3rem"
    },
    row: {
        display: "flex",
        justifyContent: "space-between",
        marginBottom: "3rem"
    },
    sectionHeader: {
        textAlign: "center"
    },
    blueBadge: {
        backgroundColor: dolphinTokens.colorLightBlue,
        color: "white",
        padding: "0.5rem 1.5rem",
        borderRadius: "50px",
        fontSize: "0.875rem",
        textTransform: "uppercase",
        display: "inline-block"
    },
    cardCol: {
        maxWidth: "350px",
        width: "100%"
    },
    card: {
        display: "flex",
        flexDirection: "column",
        height: "100%",
        backgroundColor: "white",
        borderRadius: "8px",
        boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
        transition: "all 0.3s",
        overflow: "hidden",
        padding: "0",
        gap: "0",
        "&:hover": {
            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
            transform: "translateY(-4px)"
        },
        "--fui-Card--size": "0px",
        "--fui-Card--gap": "0px",
    },
    media: {
        width: "100%",
        aspectRatio: "3 / 2",
        overflow: "hidden",
        display: "block",
    },
    mediaImg: {
        width: "100%",
        height: "100%",
        objectFit: "cover",
        objectPosition: "center",
        display: "block"
    },
    cardTextWrapper: {
        padding: "0.75rem 1rem"
    },
    flexContainer: {
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "center",
        gap: "2rem"
    },
    flexLink: {
        flex: "1 1 350px",
        maxWidth: "350px",
        textDecoration: "none"
    },
    preview: {
        width: "100%",
        aspectRatio: "3 / 2",
        overflow: "hidden",
        display: "block", 
        flex: "0 0 100%"
    },
    image: {
        width: "100%",
        height: "100%",
        objectFit: "cover",
        objectPosition: "center",
        display: "block"
    },
    logo: {
        height: "32px",
        width: "auto",
        marginBottom: "1rem",
        backgroundColor: "rgba(255, 255, 255, 0.5)",
    },
    skeletons: {
        flexDirection: 'column',
        gap: tokens.spacingVerticalL,

        "& > [role='progressbar']": {
            width: '100%'
        }
    },
    viewAllContainer: {
        display: "flex",
        justifyContent: "center",
        marginTop: "2rem"
    },
    viewAllButton: {
        backgroundColor: dolphinTokens.colorLightBlue,
        color: "white",
        borderRadius: "50px",
        padding: "0.75rem 2rem",
        fontSize: "1rem",
        textDecoration: "none",
        display: "inline-block",
        transition: "all 0.3s",

        "&:hover": {
            backgroundColor: dolphinTokens.colorLightBlue,
            transform: "translateY(-2px)",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)"
        }
    },
    cardTitle: {
        fontSize: tokens.fontSizeBase300,
        lineHeight: tokens.lineHeightBase300,
        fontWeight: tokens.fontWeightSemibold,
        display: "block",
        marginBottom: "0.25rem"
    },
    cardDescription: {
        fontSize: tokens.fontSizeBase300,
        lineHeight: tokens.lineHeightBase300,
        fontWeight: tokens.fontWeightRegular,
        display: "block"
    }
});

const Placeholder = () => {
    const styles = useStyles();
    const cardClassName = mergeClasses(styles.card, styles.skeletons);

    const firstSkeletonClassName = mergeStyles({ height: '224px !important' });

    return (
        <div className={styles.cardCol}>
            <Card className={cardClassName}>
                <SkeletonItem shape="rectangle" className={firstSkeletonClassName} />
                <SkeletonItem size={36} shape="rectangle" />
                <SkeletonItem size={20} shape="rectangle" />
            </Card>
        </div>
    );
};

const ProductPage = (props: { productPage: IPagePreview }) => {
    const { productPage } = props;
    const styles = useStyles();

    return (
        <Link className={styles.flexLink} to={`/products/${productPage.slug}`}>
            <Card className={styles.card}>
                <CardPreview className={styles.media}>
                    <img
                        src={productPage.heroImageUrl}
                        alt={productPage.heroTitle}
                        className={styles.mediaImg}
                        loading="lazy"
                    />
                </CardPreview>
                <div className={styles.cardTextWrapper}>
                    <Text as="span" block className={styles.cardTitle}>
                        {productPage.heroTitle}
                    </Text>

                    <Text as="span" block className={styles.cardDescription}>
                        {productPage.heroDescription}
                    </Text>
                </div>
            </Card>
        </Link>
    );
};

export default function ProductSection() {
    const { t } = useTranslation("home");
    const styles = useStyles();

    const [status, setStatus] = useState<Status>(Status.LOADING);
    const [productPages, setProductPages] = useState<IPagePreview[]>([]);

    useEffect(() => {
        getProductPage(
            (pages) => {
                const shuffled = shuffle(pages);
                setProductPages(shuffled);
            },
            setStatus
        );
    }, []);

    if (status === Status.LOAD_ERROR) {
        return null!
    }

    return (
        <div className={styles.contentWrapper}>
            <Text as="h2" size={700} align="center" block>
                {t("productSection.title")}
            </Text>
            <Text as="h5" size={500} align="center" block>
                {t("productSection.subtitle")}
            </Text>
            <div className={styles.flexContainer}>
                {
                    status === Status.LOADING &&
                    Array.from({ length: 3 }, (_, index) => <Placeholder key={index} />)
                }
                
                {
                    status === Status.LOAD_SUCCESS &&
                    productPages
                        .slice(0, 3)
                        .map((productPage) => <ProductPage key={productPage.id} productPage={productPage} />)
                }
            </div>
            <div className={styles.viewAllContainer}>
                <Link to="/products" className={styles.viewAllButton}>
                    {t("productSection.viewAll")}
                </Link>
            </div>
        </div>
    );
}

async function getProductPage(
    setProductPages: (productPages: IPagePreview[]) => void,
    setStatus: (status: Status) => void
) {
    try {
        const response = await pageService.getPagePreviews();

        setProductPages(response);
        setStatus(Status.LOAD_SUCCESS);
    } catch (_) {
        setStatus(Status.LOAD_ERROR);
    }
}

function shuffle<T>(array: T[]): T[] {
    const result = [...array];
    for (let i = result.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [result[i], result[j]] = [result[j], result[i]];
    }
    return result;
}